import type { RouteObject } from 'react-router';
import { AdminGate } from '@/app/routing/admin-gate';
import { RequireAuth } from '@/app/routing/require-auth';
import { OrgIndexPage, RequireOrg, RequireWorkspace } from '@/app/routing/require-org';
import { NotFoundPage } from '@/widgets/not-found';
import { AccountPage } from '../pages/account';
import { CheckEmailPage } from '../pages/check-email';
import { HomePage } from '../pages/home';
import { InvitePage } from '../pages/invite';
import { OrderPayPage } from '../pages/order-pay';
import { OrgOrdersPage } from '../pages/orders';
import {
  OrgBillingPage,
  OrgGeneralPage,
  OrgMembersPage,
  OrgSettingsIndex,
  OrgWorkspacesPage,
  WorkspaceSettingsPage,
} from '../pages/org-settings';
import { ProjectPage } from '../pages/project';
import { ProjectsPage } from '../pages/projects';
import { SignInPage } from '../pages/sign-in';
import { SignUpPage } from '../pages/sign-up';

/**
 * The route table. The active org and workspace live in the URL (Q4): `/o/:orgSlug/w/:workspaceSlug/...`.
 * `signedInExtras` is how `router.tsx` adds the dev-only design reference: the dynamic import has to sit
 * behind the build-time constant there, or the production bundle would carry the page.
 */
export function appRoutes({
  signedInExtras = [],
}: {
  signedInExtras?: RouteObject[];
} = {}): RouteObject[] {
  return [
    { path: '/sign-in', element: <SignInPage /> },
    { path: '/sign-up', element: <SignUpPage /> },
    { path: '/check-email', element: <CheckEmailPage /> },
    // the link in an invitation email: works signed out and signed in, so it sits outside RequireAuth
    { path: '/invite/:token', element: <InvitePage /> },
    // staff area: the gate is all customers download; the rest is one lazy chunk
    { path: '/admin/*', element: <AdminGate /> },
    {
      element: <RequireAuth />,
      children: [
        { path: '/', element: <HomePage /> },
        { path: '/account', element: <AccountPage /> },
        {
          path: '/o/:orgSlug',
          element: <RequireOrg />,
          children: [
            { index: true, element: <OrgIndexPage /> },
            {
              path: 'settings',
              children: [
                { index: true, element: <OrgSettingsIndex /> },
                { path: 'general', element: <OrgGeneralPage /> },
                { path: 'members', element: <OrgMembersPage /> },
                { path: 'workspaces', element: <OrgWorkspacesPage /> },
                { path: 'workspaces/:workspaceSlug', element: <WorkspaceSettingsPage /> },
                { path: 'billing', element: <OrgBillingPage /> },
                { path: '*', element: <NotFoundPage /> },
              ],
            },
            { path: 'orders', element: <OrgOrdersPage /> },
            { path: 'orders/:orderId/pay', element: <OrderPayPage /> },
            {
              path: 'w/:workspaceSlug',
              element: <RequireWorkspace />,
              children: [
                { index: true, element: <ProjectsPage /> },
                { path: 'projects/:projectId', element: <ProjectPage /> },
              ],
            },
            { path: '*', element: <NotFoundPage /> },
          ],
        },
        ...signedInExtras,
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
